import type { MfgPurchaseOrder, MfgPurchaseOrderLine } from "@/lib/manufacturing/purchaseOrderTypes";

export type RenderPurchaseOrderPdfArgs = {
  order: MfgPurchaseOrder;
  lines: MfgPurchaseOrderLine[];
  plantName?: string;
};

export type RenderPurchaseOrderPdfResult =
  | { ok: true; blob: Blob }
  | { ok: false; error: string };

/** Lazy-load @react-pdf/renderer for purchase-order export. */
export async function renderPurchaseOrderPdfToBlob(
  args: RenderPurchaseOrderPdfArgs
): Promise<RenderPurchaseOrderPdfResult> {
  try {
    const [{ pdf }, { PurchaseOrderPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("./PurchaseOrderPdf"),
    ]);
    const blob = await pdf(
      PurchaseOrderPdf({
        order: args.order,
        lines: args.lines,
        plantName: args.plantName,
      })
    ).toBlob();
    return { ok: true, blob };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Failed to render purchase order PDF",
    };
  }
}

export function downloadPdfBlob(blob: Blob, poNumber: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `PO-${poNumber || "draft"}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadPurchaseOrderPdf(args: RenderPurchaseOrderPdfArgs): Promise<
  | { ok: true }
  | { ok: false; error: string }
> {
  const rendered = await renderPurchaseOrderPdfToBlob(args);
  if (!rendered.ok) return rendered;
  downloadPdfBlob(rendered.blob, args.order.po_number || "draft");
  return { ok: true };
}
